import { theme } from 'antd';
import type { ThemeConfig } from 'antd';
import { darken, lighten, mix, toHex } from 'color2k';

// Theme constants
const primary = '#0284c7';
const darkBg = '#1c2934';
const darkBgAlt = '#21262b';
const lightBg = toHex(lighten(darkBg, 0.9));
const lightBgAlt = toHex(lighten(darkBgAlt, 0.5));
const borderBase = toHex(lighten(darkBg, 0.2));
const lightContainerBase = toHex(lighten(lightBg, 0.03));
const darkContainerBase = toHex(lighten(darkBg, 0.03));
const tooltipBase = toHex(lighten(darkBg, 0.25));
const linkHoverBase = toHex(lighten(primary, 0.18));

const lightTheme = {
  primary,
  bg: lightBg,
  bgDark: lightBgAlt,
  bgDarker: toHex(darken(lightBgAlt, 0.03)),
  bgDarkest: toHex(darken(lightBgAlt, 0.06)),
  borderColor: borderBase,
  elevatedColor: toHex(lighten(lightBg, 0.12)),
  containerColor: lightContainerBase,
  tooltipColor: tooltipBase,
  linkHover: linkHoverBase,
  modalElevated: '#ffffff',
  tabTextColor: toHex(darken(lightBgAlt, 0.06)),
  tabColorPrimary: primary,
};

const darkTheme = {
  primary,
  bg: darkBg,
  bgDark: darkBgAlt,
  bgDarker: toHex(darken(darkBgAlt, 0.03)),
  bgDarkest: toHex(darken(darkBgAlt, 0.06)),
  borderColor: borderBase,
  elevatedColor: toHex(lighten(darkBg, 0.12)),
  containerColor: darkContainerBase,
  tooltipColor: tooltipBase,
  linkHover: linkHoverBase,
  modalElevated: darkContainerBase,
  tabTextColor: mix('#EEE', primary, 0.33),
  tabColorPrimary: '#ffffff',
};

export function getTheme(isDark: boolean): ThemeConfig {
  const th = isDark ? darkTheme : lightTheme;
  return {
    hashed: false,
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: th.primary,
      colorBgLayout: th.bg,
      colorBgBase: th.bg,
      fontFamily: 'ProximaNova, sans-serif',
      // These tokens are used by SkyListyHeader for header backgrounds:
      // - Card?.headerBg: modal header background
      // - colorBgLayout / colorBgBase: non-modal header background
      // Keep these tokens in sync with SkyListyHeader; update SkyListyHeader before changing or removing them.

      // forms
      fontSizeLG: 18,
      colorBgContainer: th.bg, // control background color
      /* borders and lines */
      colorBorder: th.borderColor,
      colorBorderSecondary: th.borderColor,
      colorSplit: th.borderColor,
      colorBgElevated: th.elevatedColor,

      // Breakpoints are duplicated across the codebase — keep these in sync when changing any:
      //   - apps/dashboard/src/index.css (Tailwind `--breakpoint-*`)
      //   - packages/utils/testing/src/mock-match-media.ts (`BREAKPOINT_WIDTHS`, test-only)
      screenXSMax: 639,
      screenSM: 640,
      screenSMMin: 640,
      screenMD: 768,
      screenMDMin: 768,
      screenLG: 1024,
      screenLGMin: 1024,
      screenXL: 1300,
      screenXLMin: 1300,
      screenXXL: 1600,
      screenXXLMin: 1600,
    },
    components: {
      Typography: {
        colorLink: isDark ? th.primary : undefined,
        colorLinkHover: isDark ? lighten(th.primary, 0.1) : undefined,
        colorLinkActive: isDark ? darken(th.primary, 0.1) : undefined,
      },
      Layout: {
        headerBg: th.bgDarker,
        siderBg: th.bgDarker,
      },
      Menu: {
        itemHoverBg: 'transparent',
        itemSelectedColor: '#ffffff',
        subMenuItemSelectedColor: '#ffffff',
        darkItemBg: th.bgDarker,
        darkSubMenuItemBg: th.bgDarker,
        itemHeight: 56,
      },
      Table: {
        fontSize: 14,
        borderRadiusLG: 16,
        colorBgContainer: th.containerColor,
        bodySortBg: 'rgba(255, 255, 255, 0.04)',
        rowHoverBg: 'rgba(255, 255, 255, 0.04)',
        headerBg: 'rgba(255, 255, 255, 0.04)',
        headerSortActiveBg: 'rgba(255, 255, 255, 0.1)',
        headerSortHoverBg: 'rgba(255, 255, 255, 0.1)',
      },
      Form: {
        labelHeight: 56,
      },
      Input: {
        controlHeightLG: 56,
      },
      DatePicker: {
        controlHeightLG: 56,
        colorLink: isDark ? lighten(th.primary, 0.3) : undefined,
        colorLinkHover: isDark ? lighten(th.primary, 0.2) : undefined,
        colorLinkActive: isDark ? lighten(th.primary, 0.1) : undefined,
      },
      Segmented: {
        controlHeightLG: 48,
      },
      InputNumber: {
        controlHeightLG: 56,
      },
      Select: {
        controlHeightLG: 56,
        fontSizeIcon: 16,

        // we should compensate the font size difference between default 16px and our 18px, to make the single and multiple selects height equal
        lineHeightLG: (1.5 * 16) / 18,

        // optionLineHeight: 1.56,
        // optionPadding: '5px 12px',
        // optionFontSize: 14,
      },
      Button: {
        fontSizeLG: 14,
        controlHeightLG: 60,
        paddingInlineLG: 30,
        colorLink: isDark ? lighten(th.primary, 0.02) : undefined,
        colorLinkHover: isDark ? th.linkHover : undefined,
        colorLinkActive: isDark ? th.primary : undefined,
      },
      Tabs: {
        colorText: th.tabTextColor,
        colorPrimary: th.tabColorPrimary,
      },
      Modal: {
        colorBgElevated: th.modalElevated,
      },
      Card: {
        colorText: mix('#EEE', primary, 0.33),
        colorBgContainer: th.containerColor,
        // Card.headerBg is used by SkyListyHeader as modal header background.
        headerBg: th.containerColor,
      },
      Tooltip: {
        colorBgSpotlight: th.tooltipColor,
      },
      Slider: {
        dotSize: 8,
        handleSize: 11,
        handleSizeHover: 13,
        railSize: 2,
        handleActiveColor: '#ffffff',
        handleColor: '#ffffff',
        dotActiveBorderColor: '#ffffff',
        trackBg: '#ffffff',
        trackHoverBg: '#ffffff',
      },
      Radio: {
        colorPrimary: '#fff',
        buttonBg: 'transparent',
        buttonColor: th.tooltipColor,
        colorPrimaryHover: '#fff',
        buttonSolidCheckedColor: th.bg,
        radioSize: 12,
      },
    },
  };
}
